import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { getCachedSignedUrl } from '@mcn-platform/models';
import VideoPlayer from '../CustomizedPlayer/VideoPlayer';
/**
 * SignedVideoPlayer - Video player that uses AWS S3 signed URLs for playback
 *
 * This component handles the generation and management of pre-signed S3 URLs
 * for secure video and poster image access. It wraps the base VideoPlayer
 * component and manages URL signing lifecycle.
 *
 * **Features:**
 * - Automatic signed URL generation for video and poster
 * - URL caching to minimize signing requests
 * - Automatic URL regeneration on playback errors (for expired URLs)
 * - Lazy loading - only renders when video URL is available
 *
 * **Error Recovery:**
 * If a playback error occurs (e.g., URL expiration), the component automatically
 * requests a new signed URL and attempts to recover playback.
 *
 * @param bucket - S3 bucket containing the video
 * @param path - S3 path to the video file
 * @param bucketPoster - Optional S3 bucket for poster image
 * @param pathPoster - Optional S3 path to poster image
 * @param showPlayerControls - Whether to show video controls
 * @param ...baseProps - Standard video player properties
 *
 * @example
 * ```tsx
 * <SignedVideoPlayer
 *   bucket="my-video-bucket"
 *   path="videos/lesson1.mp4"
 *   bucketPoster="my-thumbnails"
 *   pathPoster="thumbnails/lesson1.jpg"
 *   showPlayerControls={true}
 *   onEnd={() => console.log('Video complete')}
 * />
 * ```
 */
const SignedVideoPlayer = ({ bucket, path, bucketPoster, pathPoster, onProgress, onEnd, onPause, onDuration, handleNextPrevious, showPlayerControls, seekPosition, initialMaxPosition, seekTrigger, aspectRatio, playerStyle, disableSeekAhead, }) => {
    const [url, setUrl] = useState();
    const [posterUrl, setPosterUrl] = useState();
    // console.log(`Rendering with bucket ${bucket} and key ${path}`);
    useEffect(() => {
        if (!bucket || !path) {
            return;
        }
        getCachedSignedUrl(bucket, path).then((val) => {
            // console.log('setting video url to', val);
            setUrl(val);
        });
    }, [bucket, path]);
    useEffect(() => {
        if (!bucketPoster || !pathPoster) {
            return;
        }
        getCachedSignedUrl(bucketPoster, pathPoster).then((val) => {
            // console.log('setting poster url to', val);
            setPosterUrl(val);
        });
    }, [bucketPoster, pathPoster]);
    if (!url) {
        return null;
    }
    const handleError = (error) => {
        console.error('VideoPlayer error', error);
        // try and recover by resigning the url, then pause and play
        getCachedSignedUrl(bucket, path).then((val) => {
            setUrl(val);
        });
    };
    return (_jsx(VideoPlayer, { url: url, posterUrl: posterUrl, onDuration: onDuration, onEnd: onEnd, onError: handleError, onPause: onPause, onProgress: onProgress, showControls: showPlayerControls, seekPosition: seekPosition, initialMaxPosition: initialMaxPosition, seekTrigger: seekTrigger, handleNextPrevious: handleNextPrevious, aspectRatio: aspectRatio, playerStyle: playerStyle, disableSeekAhead: disableSeekAhead }));
};
SignedVideoPlayer.defaultProps = {
    bucketPoster: undefined,
    pathPoster: undefined,
    showPlayerControls: undefined,
};
export default observer(SignedVideoPlayer);
//# sourceMappingURL=SignedVideoPlayer.js.map